import { useState, type ReactNode } from "react";
import { Save, Trash2, X } from "lucide-react";

/**
 * 管理者用の全画面エディタの器。
 *
 * Team*EditModal 8 本が、同じヘッダーと同じ削除確認ダイアログを
 * それぞれ書いていた。合計 2,354 行のうち、この器の部分が
 * 1 本あたり 60〜80 行を占めていた。
 *
 * 中身 (どんな入力欄が並ぶか) は本ごとに違うので children のまま。
 * 揃えるのは器だけにする。
 *
 * レッスン編集側にも AdminLessonEditModal という同じ器が別に立っていた。
 * そちらは API を変えずにこの器へ委譲させてある。
 */
export function AdminEditorShell({
  title,
  subtitle,
  isSaving,
  canSave = true,
  onClose,
  onSave,
  onDelete,
  deleteTargetName,
  deleteMessage,
  headerExtra,
  layout = "scroll",
  saveLabel = "保存",
  extraHeaderActions,
  showActionIcons = true,
  children,
}: {
  /** ヘッダーの見出し。「新規案件を作成」など */
  title: string;
  /** 見出しの右に薄字で添える補足。レッスン名など */
  subtitle?: string;
  isSaving: boolean;
  /** 保存ボタンの活性。必須項目が空なら false を渡す */
  canSave?: boolean;
  onClose: () => void;
  onSave: () => void;
  /** 渡すと削除ボタンが出る。新規作成のときは渡さない */
  onDelete?: () => void;
  /** 削除確認に出す名前。「〇〇 を削除しますか」 */
  deleteTargetName?: string;
  /** 名前で言い表せないとき。「このメンタリング枠を削除しますか」など */
  deleteMessage?: string;
  /** タブなど、見出しの下に敷きたいもの */
  headerExtra?: ReactNode;
  /**
   * scroll は縦に流す 1 枚。split は左右 2 ペイン (メタ情報 + Markdown)。
   * bare は器を挟まず children をそのまま置く。中身が自分で
   * flex-1 min-h-0 を持っているとき用
   */
  layout?: "scroll" | "split" | "bare";
  /** 保存ボタンの文言。保存中は常に「保存中...」になる */
  saveLabel?: string;
  /** キャンセルの左に並べる追加のボタン */
  extraHeaderActions?: ReactNode;
  /**
   * ボタンにアイコンを出すか。レッスン編集側は文字だけだったので、
   * 見た目を変えずに寄せるための逃げ道
   */
  showActionIcons?: boolean;
  children: ReactNode;
}) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-white">
      <div className="flex items-center justify-between px-6 py-4 border-b border-main bg-white shrink-0">
        <div className="flex items-center gap-3">
          <span className="px-2 py-0.5 bg-[#363635] text-white text-xs font-bold rounded">
            ADMIN
          </span>
          <h2 className="text-lg font-bold">{title}</h2>
          {subtitle && <span className="text-sm text-gray-500">{subtitle}</span>}
        </div>
        <div className="flex items-center gap-2">
          {extraHeaderActions}
          {onDelete && (
            <button
              onClick={() => setConfirmingDelete(true)}
              disabled={isSaving}
              className="px-4 py-2 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              削除
            </button>
          )}
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm border border-dark rounded-lg hover:bg-main disabled:opacity-50 flex items-center gap-2"
          >
            {showActionIcons && <X className="w-4 h-4" />}
            キャンセル
          </button>
          <button
            onClick={onSave}
            disabled={isSaving || !canSave}
            className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-[#049a4e] disabled:opacity-50 flex items-center gap-2"
          >
            {showActionIcons && <Save className="w-4 h-4" />}
            {isSaving ? "保存中..." : saveLabel}
          </button>
        </div>
      </div>

      {headerExtra}

      {layout === "bare" ? (
        children
      ) : (
        <div
          className={
            layout === "split"
              ? "flex-1 flex overflow-hidden"
              : "flex-1 overflow-y-auto"
          }
        >
          {children}
        </div>
      )}

      {confirmingDelete && onDelete && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-bold mb-4">削除の確認</h3>
            <p className="text-sub mb-6">
              {deleteMessage ||
                (deleteTargetName
                  ? `「${deleteTargetName}」を削除しますか？この操作は取り消せません。`
                  : "削除しますか？この操作は取り消せません。")}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmingDelete(false)}
                className="px-4 py-2 border border-dark rounded-lg hover:bg-main"
              >
                キャンセル
              </button>
              <button
                onClick={onDelete}
                disabled={isSaving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** 入力欄を縦に並べる、中身の既定の器。8 本すべてが同じ幅と間隔だった */
export function AdminEditorBody({ children }: { children: ReactNode }) {
  return <div className="max-w-2xl mx-auto p-6 space-y-6">{children}</div>;
}

/** 左右 2 ペインの中身。左にメタ情報、右に Markdown エディタが入る */
export function AdminEditorSplit({
  metaTitle,
  mainTitle,
  meta,
  children,
}: {
  metaTitle: string;
  mainTitle: string;
  /** 左ペインに縦に並べる入力欄 */
  meta: ReactNode;
  /** 右ペインの中身。MarkdownEditor が入る */
  children: ReactNode;
}) {
  return (
    <>
      <div className="w-[400px] border-r border-main overflow-y-auto bg-white shrink-0">
        <PaneHeader>{metaTitle}</PaneHeader>
        <div className="p-4 space-y-4">{meta}</div>
      </div>
      <div className="flex-1 flex flex-col overflow-hidden bg-white">
        <PaneHeader>{mainTitle}</PaneHeader>
        <div className="flex-1 p-4 overflow-hidden">{children}</div>
      </div>
    </>
  );
}

function PaneHeader({ children }: { children: ReactNode }) {
  return (
    <div className="p-4 border-b border-main bg-main">
      <h3 className="text-sm font-bold text-main">{children}</h3>
    </div>
  );
}
